import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, FilePdf, Printer, DownloadSimple, Plus, PencilSimple, Trash, X, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { printPdf as printPdfUtil, downloadPdf as downloadPdfUtilFn } from '../utils/pdfUtils';

import { ReserveChargesSection } from '../components/ReserveChargesSection';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTH_KEYS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];

const currentYear = new Date().getFullYear();

const formatDateDMY = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

export default function ReserveFundStatement() {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Phase 953 — Date/Year Range Selector.
  //   mode = 'year' | 'date'  (default 'year')
  //   Year Range:  fromYear / toYear (integers)
  //   Date Range:  fromDate / toDate (YYYY-MM-DD)
  // Persisted per-browser so the choice survives navigation.
  const [rangeMode, setRangeMode] = useState(() => {
    try { return localStorage.getItem('rf.rangeMode') || 'year'; } catch { return 'year'; }
  });
  const [fromYear, setFromYear] = useState(() => {
    try { return parseInt(localStorage.getItem('rf.fromYear'), 10) || (currentYear - 1); } catch { return currentYear - 1; }
  });
  const [toYear, setToYear] = useState(() => {
    try { return parseInt(localStorage.getItem('rf.toYear'), 10) || currentYear; } catch { return currentYear; }
  });
  const [fromDate, setFromDate] = useState(() => {
    try { return localStorage.getItem('rf.fromDate') || `${currentYear - 1}-01-01`; } catch { return `${currentYear - 1}-01-01`; }
  });
  const [toDate, setToDate] = useState(() => {
    try { return localStorage.getItem('rf.toDate') || `${currentYear}-12-31`; } catch { return `${currentYear}-12-31`; }
  });
  useEffect(() => { try { localStorage.setItem('rf.rangeMode', rangeMode); } catch { /* ignore */ } }, [rangeMode]);
  useEffect(() => { try { localStorage.setItem('rf.fromYear', String(fromYear)); } catch { /* ignore */ } }, [fromYear]);
  useEffect(() => { try { localStorage.setItem('rf.toYear', String(toYear)); } catch { /* ignore */ } }, [toYear]);
  useEffect(() => { try { localStorage.setItem('rf.fromDate', fromDate); } catch { /* ignore */ } }, [fromDate]);
  useEffect(() => { try { localStorage.setItem('rf.toDate', toDate); } catch { /* ignore */ } }, [toDate]);

  // Build query params for the statement API depending on the active mode.
  const rangeParams = useMemo(() => {
    if (rangeMode === 'date') {
      return { from_date: fromDate, to_date: toDate };
    }
    return { from_year: fromYear, to_year: toYear };
  }, [rangeMode, fromYear, toYear, fromDate, toDate]);
  const rangeQuery = useMemo(() => {
    return Object.entries(rangeParams).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  }, [rangeParams]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Phase 946 — Annual Expenses Difference (Buildings only).
  const [adjustments, setAdjustments] = useState([]);
  const [adjEditing, setAdjEditing] = useState(null); // {adjustment_id?, year, category, amount, notes}

  const fetchAdjustments = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/buildings/${buildingId}/annual-adjustments`);
      setAdjustments(r.data?.items || []);
    } catch (_) { /* ignore */ }
  }, [buildingId]);
  useEffect(() => { fetchAdjustments(); }, [fetchAdjustments]);
  const [generated, setGenerated] = useState(false);

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/reserve-fund-statement?${rangeQuery}`);
      setData(res.data);
      setGenerated(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    downloadPdfUtilFn(`/buildings/${buildingId}/reserve-fund-statement/pdf`, { ...rangeParams, lang: language });
    toast.success(t('exportSuccess'));
  };

  const handlePrintPDF = () => {
    printPdfUtil(`/buildings/${buildingId}/reserve-fund-statement/pdf`, { ...rangeParams, lang: language });
  };

  // Phase 946 — Save (create or update) an Annual Expenses Difference line.
  const saveAdjustment = async () => {
    const a = adjEditing;
    if (!a) return;
    const y = parseInt(a.year, 10);
    const amt = parseFloat(a.amount);
    if (!y || !Number.isFinite(y) || y < 2000 || y > 2100) { toast.error(language === 'en' ? 'Invalid year' : 'Μη έγκυρο έτος'); return; }
    if (!(a.category || '').trim()) { toast.error(language === 'en' ? 'Category required' : 'Απαιτείται κατηγορία'); return; }
    if (!Number.isFinite(amt)) { toast.error(language === 'en' ? 'Amount required' : 'Απαιτείται ποσό'); return; }
    const body = { year: y, category: a.category.trim(), amount: amt, notes: (a.notes || '').trim() };
    try {
      if (a.adjustment_id) {
        await axios.put(`${API}/buildings/${buildingId}/annual-adjustments/${a.adjustment_id}`, body);
      } else {
        await axios.post(`${API}/buildings/${buildingId}/annual-adjustments`, body);
      }
      toast.success(language === 'en' ? 'Saved' : 'Αποθηκεύτηκε');
      setAdjEditing(null);
      await fetchAdjustments();
      if (generated) fetchStatement();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  const deleteAdjustment = async (aid) => {
    if (!window.confirm(language === 'en' ? 'Delete this adjustment?' : 'Διαγραφή προσαρμογής;')) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/annual-adjustments/${aid}`);
      toast.success(language === 'en' ? 'Deleted' : 'Διαγράφηκε');
      await fetchAdjustments();
      if (generated) fetchStatement();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Phase 946 — Group current adjustments by year for inline display in
  // this Reserve Fund Statement page, regardless of whether a full
  // statement report has been generated.
  const adjustmentsByYear = useMemo(() => {
    const m = new Map();
    (adjustments || []).forEach(a => {
      const y = a.year;
      if (!m.has(y)) m.set(y, []);
      m.get(y).push(a);
    });
    return Array.from(m.entries()).sort((a, b) => b[0] - a[0]);
  }, [adjustments]);

  // Phase 815 — Helper that renders a list of RF repair rows. Extracted
  // so both the single-year "period" view AND per-year sub-sections in
  // multi-year reports can render the same rich layout without
  // duplicating the JSX.
  const renderRepairsList = (repairs, keyPrefix = 'rfu') => {
    if (!repairs || repairs.length === 0) {
      return <p className="text-sm text-zinc-400 italic py-2">{t('noRepairsPaidFromReserve')}</p>;
    }
    return (
      <div className="pt-1" data-testid={`${keyPrefix}-repairs-list`}>
        <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">{t('repairsThroughReserve')}</p>
        <div className="divide-y divide-zinc-100">
          {repairs.map((r, idx) => {
            const dateStr = r.date && r.date.length === 10
              ? `${r.date.slice(8,10)}/${r.date.slice(5,7)}/${r.date.slice(0,4)}`
              : (r.date || '');
            const isElevator = r.source === 'elevator';
            const refLabel = !isElevator ? (r.rf_reference || r.reference_number || '') : '';
            const invoiceItems = isElevator
              ? (Array.isArray(r.elevator_items) && r.elevator_items.length > 0
                  ? r.elevator_items
                  : (r.rf_description ? String(r.rf_description).split(/\r?\n/).map(s => s.trim()).filter(Boolean) : []))
              : [];
            const invoiceNumber = isElevator ? (r.elevator_invoice_number || '') : '';
            return (
              <div key={idx} className="py-2 text-sm" data-testid={`${keyPrefix}-repair-${idx}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    {isElevator ? (
                      invoiceItems.length > 0 ? (
                        <div className="space-y-1.5" data-testid={`${keyPrefix}-repair-items-${idx}`}>
                          {invoiceItems.map((line, li) => (
                            <div key={li} data-testid={`${keyPrefix}-repair-item-${idx}-${li}`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                {li === 0 && dateStr && <span className="text-[11px] font-mono text-zinc-500">{dateStr}</span>}
                                <span className="font-medium text-zinc-800">{line}</span>
                              </div>
                              {invoiceNumber && (
                                <div className="text-[10px] font-mono text-zinc-400 mt-0.5">
                                  {t('invoice') || 'Invoice'} #{invoiceNumber}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          {dateStr && <span className="text-[11px] font-mono text-zinc-500">{dateStr}</span>}
                          <span className="font-medium text-zinc-800">{r.description}</span>
                        </div>
                      )
                    ) : (
                      <>
                        <div className="flex items-center gap-2 flex-wrap">
                          {dateStr && <span className="text-[11px] font-mono text-zinc-500">{dateStr}</span>}
                          <span className="font-medium text-zinc-800">{r.description}</span>
                        </div>
                        {r.rf_description && (
                          <div className="mt-0.5 text-[11px] text-zinc-600 italic whitespace-pre-line" data-testid={`${keyPrefix}-repair-notes-${idx}`}>
                            {r.rf_description}
                          </div>
                        )}
                        {refLabel && (
                          <div className="mt-0.5 text-[10px] font-mono text-zinc-400">
                            {t('reference') || 'Reference'}: {refLabel}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <span className="font-mono text-red-700 whitespace-nowrap pt-0.5">(&euro;{(r.amount || 0).toFixed(2)})</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const _fmtDMY = (d) => (d && d.length === 10 ? `${d.slice(8,10)}/${d.slice(5,7)}/${d.slice(0,4)}` : (d || ''));
  const isMultiYear = data?.yearly_sections && data.yearly_sections.length > 1;

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 py-6" data-testid="reserve-fund-statement-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/buildings/${buildingId}`)} className="p-2 hover:bg-zinc-100 transition-colors" data-testid="reserve-back-btn">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('reserveFundStatement')}</h1>
              {data?.building && <p className="text-sm text-zinc-500">{data.building.name} — {data.building.location}</p>}
            </div>
          </div>
          {generated && data && (
            <div className="flex gap-2">
              <Button onClick={() => navigate('/reserve-split-fix')} variant="outline" className="rounded-none" data-testid="reserve-split-fix-link">
                {language === 'el' ? 'Διόρθωση κατανομής αποθεματικού' : 'Reserve split correction'}
              </Button>
              <Button onClick={handlePrintPDF} variant="outline" className="rounded-none" data-testid="reserve-print-pdf">
                <Printer size={18} className="mr-2" />{t('printPdf')}
              </Button>
              <Button onClick={handleExportPDF} className="bg-red-700 hover:bg-red-800 rounded-none" data-testid="reserve-export-pdf">
                <DownloadSimple size={18} className="mr-2" />{t('downloadPdf')}
              </Button>
            </div>
          )}
        </div>

        {/* Controls — Phase 953 Range Selector */}
        <div className="bg-white border border-zinc-200 p-5 mb-6" data-testid="rf-range-controls">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            {/* Mode toggle */}
            <div className="flex flex-col">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                {language === 'el' ? 'Τύπος περιόδου' : 'Period type'}
              </label>
              <div className="inline-flex border border-zinc-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setRangeMode('year')}
                  className={`text-xs uppercase tracking-wider font-semibold px-3 py-2 transition-colors ${rangeMode === 'year' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                  data-testid="rf-mode-year"
                >
                  {language === 'el' ? 'Ετήσια' : 'Year range'}
                </button>
                <button
                  type="button"
                  onClick={() => setRangeMode('date')}
                  className={`text-xs uppercase tracking-wider font-semibold px-3 py-2 border-l border-zinc-300 transition-colors ${rangeMode === 'date' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                  data-testid="rf-mode-date"
                >
                  {language === 'el' ? 'Ημερομηνίες' : 'Date range'}
                </button>
              </div>
            </div>

            {/* Range inputs */}
            {rangeMode === 'year' ? (
              <>
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                    {language === 'el' ? 'Από έτος' : 'From year'}
                  </label>
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    value={fromYear}
                    onChange={(e) => setFromYear(parseInt(e.target.value, 10) || fromYear)}
                    className="w-24 border border-zinc-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
                    data-testid="rf-from-year"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                    {language === 'el' ? 'Έως έτος' : 'To year'}
                  </label>
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    value={toYear}
                    onChange={(e) => setToYear(parseInt(e.target.value, 10) || toYear)}
                    className="w-24 border border-zinc-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-zinc-400"
                    data-testid="rf-to-year"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                    {language === 'el' ? 'Από ημερομηνία' : 'From date'}
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-400"
                    data-testid="rf-from-date"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                    {language === 'el' ? 'Έως ημερομηνία' : 'To date'}
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-400"
                    data-testid="rf-to-date"
                  />
                </div>
              </>
            )}
            <div className="flex-1"></div>
            <Button onClick={fetchStatement} disabled={loading} className="bg-zinc-950 rounded-none" data-testid="reserve-generate-btn">
              {loading ? '...' : t('generate')}
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">
            {language === 'el'
              ? 'Το opening balance μεταφέρεται πάντα ακριβώς από πριν την αρχή της περιόδου. Δεν μηδενίζεται όταν αλλάζεις εύρος.'
              : 'The opening balance always carries forward from before the period start — never reset when the range changes.'}
          </p>
        </div>

        {/* Statement Content */}
        {generated && data && (
          <div className="space-y-8">
            <ReserveChargesSection charges={data.reserve_charges} lang={language} />
            {/* Yearly Tables */}
            {data.years.map((yr) => {
              const yd = data.yearly_data[String(yr)];
              if (!yd) return null;
              return (
                <div key={yr} className="bg-white border border-zinc-200" data-testid={`reserve-year-${yr}`}>
                  <div className="px-5 py-3 bg-zinc-900 text-white">
                    <h2 className="text-sm font-bold tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>
                      {t('savingsBalancePerApartment')} {yr}
                    </h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid={`reserve-table-${yr}`}>
                      <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-300">
                          <th className="px-2 py-2 text-left font-semibold text-zinc-600 w-10">A/A</th>
                          <th className="px-2 py-2 text-left font-semibold text-zinc-600">{t('apartment')}</th>
                          <th className="px-2 py-2 text-center font-semibold text-zinc-600 w-14">%</th>
                          {MONTH_KEYS.map((mk, i) => (
                            <th key={i} className="px-1 py-2 text-right font-semibold text-zinc-600 min-w-[70px]">
                              {t(`month_short_${i + 1}`)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {yd.rows.map((row, idx) => (
                          <tr key={row.apartment_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50'} data-testid={`reserve-row-${yr}-${idx}`}>
                            <td className="px-2 py-1.5 text-zinc-500 font-mono">{idx + 1}</td>
                            <td className="px-2 py-1.5 font-semibold">{row.apartment_number}</td>
                            <td className="px-2 py-1.5 text-center font-mono text-zinc-500">{row.share_percentage.toFixed(2)}%</td>
                            {(row.months_detail || row.months.map(v => ({ base: v || 0, extra: 0, total: v || 0 }))).map((cell, mi) => (
                              <td key={mi} className="px-1 py-1.5 text-right font-mono">
                                {cell.total > 0 ? (
                                  <div className="flex flex-col items-end leading-tight" data-testid={`reserve-cell-${yr}-${idx}-${mi}`}
                                    title={cell.extra > 0 ? `${language === 'el' ? 'Αποθεματικό' : 'Reserve'}: €${cell.base.toFixed(2)} · ${t('extraReserve')}: €${cell.extra.toFixed(2)} · ${t('total')}: €${cell.total.toFixed(2)}` : undefined}>
                                    <span className={`font-semibold ${cell.extra > 0 ? 'text-violet-700' : ''}`}>&euro;{cell.total.toFixed(2)}</span>
                                  </div>
                                ) : ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-zinc-200 border-t-2 border-zinc-400 font-bold">
                          <td className="px-2 py-2"></td>
                          <td className="px-2 py-2">{t('total')}</td>
                          <td className="px-2 py-2 text-center font-mono">{yd.rows.reduce((s, r) => s + (r.share_percentage || 0), 0).toFixed(2)}%</td>
                          {yd.monthly_totals.map((mt, mi) => {
                            const extra = (yd.monthly_extra_totals || [])[mi] || 0;
                            return (
                              <td key={mi} className="px-1 py-2 text-right font-mono">
                                {mt > 0 ? (
                                  <div className="flex flex-col items-end leading-tight" data-testid={`reserve-total-${yr}-${mi}`}
                                    title={extra > 0 ? `${language === 'el' ? 'Αποθεματικό' : 'Reserve'}: €${(mt - extra).toFixed(2)} · ${t('extraReserve')}: €${extra.toFixed(2)} · ${t('total')}: €${mt.toFixed(2)}` : undefined}>
                                    <span>&euro;{mt.toFixed(2)}</span>
                                  </div>
                                ) : ''}
                              </td>
                            );
                          })}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {/* Phase 946/947 — Annual Expenses Difference for THIS
                      specific year, rendered directly beneath the year's
                      table so users see the adjustment in context of the
                      corresponding year. Positive amounts add to the
                      year's Reserve Fund; negative amounts subtract.
                      Buildings only. */}
                  <div className="border-t-2 border-red-100" data-testid={`annual-adj-block-${yr}`}>
                    <div className="px-5 py-2.5 bg-red-50 flex items-center justify-between border-t border-red-200">
                      <h3 className="text-xs font-bold tracking-wider text-red-900" style={{ fontFamily: 'var(--font-heading)' }}>
                        {language === 'en' ? `${yr} Annual Expenses Difference` : `Διαφορά Ετήσιων Εξόδων ${yr}`}
                      </h3>
                      <Button
                        onClick={() => setAdjEditing({ year: yr, category: '', amount: '', notes: '' })}
                        size="sm"
                        className="rounded-none bg-red-800 hover:bg-red-900 text-white h-6 text-[11px]"
                        data-testid={`add-adjustment-btn-${yr}`}
                      >
                        <Plus size={11} className="mr-1" />
                        {language === 'en' ? 'Add' : 'Προσθήκη'}
                      </Button>
                    </div>
                    {adjEditing && adjEditing.year === yr && (
                      <div className="grid grid-cols-12 gap-2 items-end px-5 py-3 bg-red-50 border-b border-red-200" data-testid={`adjustment-editor-${yr}`}>
                        <div className="col-span-3">
                          <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-0.5">{language === 'en' ? 'Category' : 'Κατηγορία'}</label>
                          <Input value={adjEditing.category} onChange={e => setAdjEditing({ ...adjEditing, category: e.target.value })} placeholder={language === 'en' ? 'e.g. Cleaning' : 'π.χ. Καθαρισμός'} className="h-8 rounded-none text-sm" data-testid="adj-category" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-0.5">{language === 'en' ? 'Amount (± €)' : 'Ποσό (± €)'}</label>
                          <Input type="number" step="0.01" value={adjEditing.amount} onChange={e => setAdjEditing({ ...adjEditing, amount: e.target.value })} placeholder="+50 or -50" className="h-8 rounded-none text-sm font-mono" data-testid="adj-amount" />
                        </div>
                        <div className="col-span-4">
                          <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-0.5">{language === 'en' ? 'Notes' : 'Σημειώσεις'}</label>
                          <Input value={adjEditing.notes} onChange={e => setAdjEditing({ ...adjEditing, notes: e.target.value })} className="h-8 rounded-none text-sm" data-testid="adj-notes" />
                        </div>
                        <div className="col-span-3 flex gap-1">
                          <Button size="sm" onClick={saveAdjustment} className="rounded-none bg-emerald-700 h-8 text-xs flex-1" data-testid="adj-save"><Check size={13} className="mr-1" />{language === 'en' ? 'Save' : 'Αποθήκευση'}</Button>
                          <Button size="sm" variant="outline" onClick={() => setAdjEditing(null)} className="rounded-none h-8 text-xs" data-testid="adj-cancel"><X size={13} /></Button>
                        </div>
                      </div>
                    )}
                    {(() => {
                      const items = (adjustments || []).filter(a => a.year === yr);
                      if (items.length === 0) {
                        return (
                          <div className="px-5 py-3 text-[11px] italic text-zinc-400 text-center">
                            {language === 'en' ? 'No adjustments for this year.' : 'Καμία προσαρμογή για αυτό το έτος.'}
                          </div>
                        );
                      }
                      const total = items.reduce((s, i) => s + (i.amount || 0), 0);
                      const positive = total > 0;
                      return (
                        <table className="w-full text-sm">
                          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                            <tr>
                              <th className="px-5 py-1.5 text-left">{language === 'en' ? 'Category' : 'Κατηγορία'}</th>
                              <th className="px-5 py-1.5 text-right">{language === 'en' ? 'Difference' : 'Διαφορά'}</th>
                              <th className="px-5 py-1.5 text-left">{language === 'en' ? 'Notes' : 'Σημειώσεις'}</th>
                              <th className="px-5 py-1.5 w-16"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map(it => {
                              const p = it.amount >= 0;
                              return (
                                <tr key={it.adjustment_id} className="border-t border-zinc-100" data-testid={`adjustment-row-${it.adjustment_id}`}>
                                  <td className="px-5 py-1.5 text-sm">{it.category}</td>
                                  <td className={`px-5 py-1.5 text-sm font-mono text-right ${p ? 'text-emerald-700' : 'text-red-700'}`} data-testid={`adjustment-amount-${it.adjustment_id}`}>
                                    {p ? '+' : ''}&euro;{it.amount.toFixed(2)}
                                  </td>
                                  <td className="px-5 py-1.5 text-xs text-zinc-500 max-w-xs truncate">{it.notes || '—'}</td>
                                  <td className="px-5 py-1.5 text-right whitespace-nowrap">
                                    <button className="p-1 hover:bg-zinc-100" onClick={() => setAdjEditing({ ...it })} data-testid={`adjustment-edit-${it.adjustment_id}`}><PencilSimple size={13} /></button>
                                    <button className="p-1 hover:bg-red-50 text-red-500" onClick={() => deleteAdjustment(it.adjustment_id)} data-testid={`adjustment-delete-${it.adjustment_id}`}><Trash size={13} /></button>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold" data-testid={`adjustment-year-total-row-${yr}`}>
                              <td className="px-5 py-1.5 text-xs uppercase tracking-wider">{language === 'en' ? 'Total' : 'Σύνολο'}</td>
                              <td className={`px-5 py-1.5 font-mono text-right ${positive ? 'text-emerald-800' : total < 0 ? 'text-red-800' : 'text-zinc-800'}`} data-testid={`adjustment-year-total-${yr}`}>
                                {positive ? '+' : ''}&euro;{total.toFixed(2)}
                              </td>
                              <td colSpan={2} className="text-[10px] italic text-zinc-500 px-5">
                                {positive
                                  ? (language === 'en' ? 'Surplus — increases this year\'s Reserve Fund' : 'Πλεόνασμα — αυξάνει το Αποθεματικό του έτους')
                                  : total < 0 ? (language === 'en' ? 'Deficit — reduces this year\'s Reserve Fund' : 'Έλλειμμα — μειώνει το Αποθεματικό του έτους') : ''}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </div>
              );
            })}

            {/* Phase 947 — Educational note about adjustments.
                Phase 950 — Corrected sign convention.
                Phase 952 — Extended per user request to clarify that the
                adjustment is computed at year-end against the plan active
                for that year and never retroactively re-priced. */}
            <div className="text-[11px] italic text-zinc-500 px-1 space-y-1" data-testid="annual-adjustments-note">
              {language === 'en' ? (
                <>
                  <p>
                    Annual Expenses Difference adjustments are calculated at the end of each financial year based on the Annual Expense Plan that is active for that year.
                  </p>
                  <p>
                    The plan active for a future year does not retroactively change previous years. Positive amounts (surplus, Plan &gt; Actual) increase the Reserve Fund balance; negative amounts (deficit, Actual &gt; Plan) decrease it. These adjustments appear ONLY in the Reserve Fund Statement — they do not create a Cash Receipt, Bank Receipt, expense, or payment.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Οι προσαρμογές Διαφοράς Ετήσιων Εξόδων υπολογίζονται στο τέλος κάθε οικονομικού έτους με βάση το Ετήσιο Πλάνο Εξόδων που είναι ενεργό για εκείνο το έτος.
                  </p>
                  <p>
                    Το ενεργό πλάνο ενός μελλοντικού έτους δεν αλλάζει αναδρομικά προηγούμενα έτη. Τα θετικά ποσά (πλεόνασμα, Πλάνο &gt; Πραγματικό) αυξάνουν το υπόλοιπο του Αποθεματικού· τα αρνητικά (έλλειμμα, Πραγματικό &gt; Πλάνο) το μειώνουν. Εμφανίζονται ΜΟΝΟ στην Κατάσταση Αποθεματικού — δεν δημιουργούν Ταμειακή Απόδειξη, Τραπεζική Απόδειξη, δαπάνη ή πληρωμή.
                  </p>
                </>
              )}
            </div>

            {/* Monthly Expenses Summary */}
            <div className="bg-white border border-zinc-200" data-testid="reserve-summary">
              <div className="px-5 py-3 bg-zinc-900 text-white">
                <h2 className="text-sm font-bold tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('monthlySavingsSummary')}</h2>
              </div>
              <div className="p-5 space-y-2">
                <div className="grid grid-cols-12 gap-2 pb-2 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">
                  <div className="col-span-6"></div>
                  <div className="col-span-2 text-right">{t('reserveAmountLabel')}</div>
                  <div className="col-span-2 text-right">{t('extraReserve')}</div>
                  <div className="col-span-2 text-right">{t('totalAdded')}</div>
                </div>
                {data.summary.savings_by_year.map((sy) => (
                  <div key={sy.year} className="grid grid-cols-12 gap-2 items-center py-2 border-b border-zinc-100">
                    <span className="col-span-6 text-sm font-medium">{t('savingsTotal')} {sy.year}</span>
                    <span className="col-span-2 text-right font-mono text-zinc-600" data-testid={`sy-base-${sy.year}`}>&euro;{(sy.base || 0).toFixed(2)}</span>
                    <span className="col-span-2 text-right font-mono text-violet-600" data-testid={`sy-extra-${sy.year}`}>&euro;{(sy.extra || 0).toFixed(2)}</span>
                    <span className="col-span-2 text-right font-mono font-semibold">&euro;{sy.total.toFixed(2)}</span>
                  </div>
                ))}
                <div className="grid grid-cols-12 gap-2 items-center py-3 border-t-2 border-zinc-300">
                  <span className="col-span-6 text-sm font-bold">{t('totalSavingsBeforeRepairs')}</span>
                  <span className="col-span-2 text-right font-mono font-bold text-zinc-700" data-testid="summary-total-base">&euro;{(data.summary.total_base_savings || 0).toFixed(2)}</span>
                  <span className="col-span-2 text-right font-mono font-bold text-violet-700" data-testid="summary-total-extra">&euro;{(data.summary.total_extra_savings || 0).toFixed(2)}</span>
                  <span className="col-span-2 text-right font-mono font-bold text-lg" data-testid="summary-total-all">&euro;{data.summary.total_savings.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Reserve Fund Usage — simple summary of where the money went */}
            <div className="bg-white border border-zinc-200" data-testid="reserve-fund-usage">
              <div className="px-5 py-3 bg-red-800 text-white">
                <h2 className="text-sm font-bold tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{t('reserveFundUsage')}</h2>
              </div>
              <div className="p-5 space-y-3">
                {/* Phase 814 — Opening Reserve Fund Balance carried forward
                    from the previous financial period so the report always
                    shows the true starting position (like a bank statement).
                    In multi-year mode this becomes the OVERALL opening for
                    the entire period; each sub-year has its own opening
                    inside its section below. */}
                {typeof data.opening_balance === 'number' && (
                  <div className="flex justify-between items-center py-2 border-b border-zinc-200 bg-blue-50/50 -mx-3 px-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-blue-900">{t('openingReserveFundBalance') || 'Opening Reserve Fund Balance'}</span>
                      <span className="text-[10px] text-zinc-500 mt-0.5">
                        {t('carriedForwardFromPriorPeriod') || 'Carried forward from prior period'}
                        {data.period_start ? ` — ${t('before') || 'before'} ${_fmtDMY(data.period_start)}` : ''}
                      </span>
                    </div>
                    <span
                      className={`font-mono font-bold ${data.opening_balance >= 0 ? 'text-blue-900' : 'text-red-700'}`}
                      data-testid="rfu-opening-balance"
                    >&euro;{Number(data.opening_balance).toFixed(2)}</span>
                  </div>
                )}

                {/* Phase 815 — Multi-year: render one sub-section per year
                    with its own opening → contributions → expenses →
                    closing block. Closing of year N is by construction the
                    opening of year N+1 (backend already chains them). */}
                {isMultiYear ? (
                  <div className="space-y-4" data-testid="rfu-yearly-sections">
                    {data.yearly_sections.map((sec) => (
                      <div key={sec.year} className="border border-zinc-200 -mx-3" data-testid={`rfu-year-${sec.year}`}>
                        {/* Year header */}
                        <div className="px-4 py-2 bg-zinc-900 text-white flex items-center justify-between">
                          <span className="text-sm font-bold tracking-wider" style={{ fontFamily: 'var(--font-heading)' }}>{sec.year}</span>
                          <span className="text-[10px] font-mono text-zinc-300 tabular-nums">{_fmtDMY(sec.period_start)} — {_fmtDMY(sec.period_end)}</span>
                        </div>
                        <div className="px-4 py-3 space-y-2">
                          {/* Year opening */}
                          <div className="flex justify-between items-center py-1.5 border-b border-zinc-100 bg-blue-50/40 -mx-1 px-1">
                            <span className="text-xs font-semibold text-blue-900">{t('openingReserveFundBalance') || 'Opening Balance'}</span>
                            <span
                              className={`font-mono text-sm font-bold ${sec.opening_balance >= 0 ? 'text-blue-900' : 'text-red-700'}`}
                              data-testid={`rfu-year-${sec.year}-opening`}
                            >&euro;{Number(sec.opening_balance).toFixed(2)}</span>
                          </div>
                          {/* Contributions */}
                          <div className="flex justify-between items-center py-1.5 border-b border-zinc-100">
                            <span className="text-xs">{t('contributions') || 'Contributions'}</span>
                            <span
                              className="font-mono text-sm font-semibold text-emerald-700"
                              data-testid={`rfu-year-${sec.year}-contrib`}
                            >&euro;{Number(sec.contributions).toFixed(2)}</span>
                          </div>
                          {/* Repairs list (may be empty) */}
                          {renderRepairsList(sec.repairs, `rfu-year-${sec.year}`)}
                          {/* Year expenses total */}
                          <div className="flex justify-between items-center py-1.5 border-t border-zinc-100">
                            <span className="text-xs">{t('reserveFundExpensesThisYear') || 'Reserve Fund expenses this year'}</span>
                            <span
                              className="font-mono text-sm font-semibold text-red-700"
                              data-testid={`rfu-year-${sec.year}-expenses`}
                            >(&euro;{Number(sec.expenses_total).toFixed(2)})</span>
                          </div>
                          {/* Year closing */}
                          <div className="flex justify-between items-center py-2 border-t-2 border-zinc-300 bg-emerald-50/70 -mx-1 px-1">
                            <div className="flex flex-col">
                              <span className="text-xs font-bold">{t('closingReserveFundBalance') || 'Closing Balance'}</span>
                              <span className="text-[9px] text-zinc-500 mt-0.5">
                                {t('carriedForwardToNextPeriod') || 'Carried forward to next period'}
                              </span>
                            </div>
                            <span
                              className={`font-mono text-base font-bold ${sec.closing_balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}
                              data-testid={`rfu-year-${sec.year}-closing`}
                            >&euro;{Number(sec.closing_balance).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {/* Single-year aggregate view (unchanged) */}
                    <div className="flex justify-between items-center py-2 border-b border-zinc-200">
                      <span className="text-sm font-medium">{t('totalBeforeRepairs')}</span>
                      <span className="font-mono font-semibold text-zinc-800" data-testid="rfu-before">&euro;{(data.summary.total_savings || 0).toFixed(2)}</span>
                    </div>
                    {renderRepairsList(data.repairs, 'rfu')}
                    <div className="flex justify-between items-center py-2 border-t border-zinc-200">
                      <span className="text-sm font-medium">{t('totalSpentFromReserveFund')}</span>
                      <span className="font-mono font-semibold text-red-700" data-testid="rfu-spent">(&euro;{(data.total_repairs || 0).toFixed(2)})</span>
                    </div>
                  </>
                )}

                {/* Closing / Grand Total Closing Reserve Fund Balance */}
                <div className="flex justify-between items-center py-3 border-t-2 border-zinc-800 bg-emerald-50 -mx-3 px-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold">
                      {isMultiYear
                        ? (t('grandTotalClosingBalance') || 'Grand Total Closing Reserve Fund Balance')
                        : (t('closingReserveFundBalance') || 'Closing Reserve Fund Balance')}
                    </span>
                    <span className="text-[10px] text-zinc-500 mt-0.5">
                      {t('carriedForwardToNextPeriod') || 'Carried forward to next period'}
                      {data.period_end ? ` — ${t('asOf') || 'as of'} ${_fmtDMY(data.period_end)}` : ''}
                    </span>
                  </div>
                  <span
                    className={`text-xl font-bold font-mono ${(data.closing_balance ?? data.net_savings) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}
                    data-testid="rfu-remaining"
                  >&euro;{Number(data.closing_balance ?? data.net_savings ?? 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!generated && !loading && (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-400">
            {t('reserveStatementHint')}
          </div>
        )}
      </div>
    </Layout>
  );
}
